/**
 * DateRangePicker Component
 * Custom date range picker with calendar popover and connected styling support
 */

import React, { useState, useRef, useCallback, useMemo } from 'react'
import { Input, IconButton, Typography } from '@mui/joy'
import { Popover } from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { cn } from 'utils/cn'
import { getMarketToday } from '@/utils/format-date'

interface DateRangePickerProps {
    startDate: Date | null
    endDate: Date | null
    onChange: (startDate: Date | null, endDate: Date | null) => void
    disabled?: boolean
    isConnected?: boolean
    isFirstInGroup?: boolean
    isLastInGroup?: boolean
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({ startDate, endDate, onChange, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [currentMonth, setCurrentMonth] = useState(new Date())
    const anchorRef = useRef<HTMLDivElement>(null)

    /** Format date as MM/DD/YYYY using local time (avoids UTC offset issues) */
    const formatDateLocal = useCallback((date: Date | null) => {
        if (!date) return ''
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const year = date.getFullYear()
        return `${month}/${day}/${year}`
    }, [])

    /** Display text for date range */
    const displayText = useMemo(
        () => (startDate && endDate ? `${formatDateLocal(startDate)} - ${formatDateLocal(endDate)}` : ''),
        [startDate, endDate, formatDateLocal]
    )

    /** Toggle popover open/closed */
    const handleToggle = useCallback(() => {
        if (!disabled) {
            setIsOpen(!isOpen)
        }
    }, [disabled, isOpen])

    /** Close popover */
    const handleClose = useCallback(() => {
        setIsOpen(false)
    }, [])

    /** Get calendar days for current month */
    const getDaysInMonth = useCallback((date: Date) => {
        const year = date.getFullYear()
        const month = date.getMonth()
        const firstDay = new Date(year, month, 1)
        const lastDay = new Date(year, month + 1, 0)
        const daysInMonth = lastDay.getDate()
        // Convert Sunday=0 to Monday-based: Mo=0, Tu=1, ..., Su=6
        const startingDayOfWeek = (firstDay.getDay() + 6) % 7

        const days: (number | null)[] = []
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null)
        }
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(i)
        }
        return days
    }, [])

    /** Check if date is disabled (before the market's today — NOT the browser's: a dispatcher already on
     *  Sep 1 must still be able to pick Aug 31 while the US is on it). Resolved once per mount, like the
     *  rest of the calendar's "today". */
    const marketToday = useMemo(() => getMarketToday(), [])
    const isDateDisabled = useCallback((date: Date) => date < marketToday, [marketToday])

    /** Check if date is in the selected range */
    const isDateInRange = useCallback(
        (date: Date) => {
            if (!startDate || !endDate) return false
            const dateTime = date.getTime()
            const startTime = startDate.getTime()
            const endTime = endDate.getTime()
            return dateTime > startTime && dateTime < endTime
        },
        [startDate, endDate]
    )

    /** Check if date is selected (start or end) */
    const isDateSelected = useCallback(
        (date: Date) => {
            const dateTime = date.getTime()
            if (startDate && dateTime === startDate.getTime()) return true
            if (endDate && dateTime === endDate.getTime()) return true
            return false
        },
        [startDate, endDate]
    )

    /** Handle date selection click */
    const handleDateClick = useCallback(
        (day: number) => {
            const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
            selectedDate.setHours(0, 0, 0, 0)

            if (isDateDisabled(selectedDate)) return

            // Check if endDate is actually set (not just equal to startDate, which means it's a placeholder)
            const isEndDateSet = endDate && startDate && endDate.getTime() !== startDate.getTime()

            if (!startDate || (startDate && isEndDateSet)) {
                // Start a new selection: set startDate, clear endDate
                onChange(selectedDate, null)
            } else {
                // Complete the range selection
                if (selectedDate.getTime() === startDate.getTime()) {
                    // Same date selected: set both to the same date
                    onChange(startDate, startDate)
                    handleClose()
                } else if (selectedDate < startDate) {
                    // Selected date is before startDate: swap them
                    onChange(selectedDate, startDate)
                    handleClose()
                } else {
                    // Selected date is after startDate: set as endDate
                    onChange(startDate, selectedDate)
                    handleClose()
                }
            }
        },
        [startDate, endDate, currentMonth, isDateDisabled, onChange, handleClose]
    )

    /** Navigate to previous month */
    const handlePrevMonth = useCallback(() => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))
    }, [currentMonth])

    /** Navigate to next month */
    const handleNextMonth = useCallback(() => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))
    }, [currentMonth])

    /** Memoize calendar data */
    const monthName = useMemo(() => currentMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' }), [currentMonth])
    const days = useMemo(() => getDaysInMonth(currentMonth), [currentMonth, getDaysInMonth])
    const weekDays = useMemo(() => ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'], [])

    return (
        <div ref={anchorRef} className="relative block w-full min-w-0 h-8">
            <Input
                value={displayText}
                placeholder="MM/DD/YYYY - MM/DD/YYYY"
                disabled={disabled}
                readOnly
                onClick={handleToggle}
                endDecorator={<CalendarTodayIcon sx={{ fontSize: 14, color: 'var(--lh-fg-muted)' }} />}
                sx={{
                    width: '100%',
                    height: '32px',
                    minHeight: '32px',
                    borderWidth: '1px',
                    borderRadius: '6px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontFamily: "'Inter', sans-serif",
                    boxShadow: 'none',
                    '--Input-focusedInsetShadow': 'none',
                    '--Input-focusedThickness': '1px',
                    '--Input-focusedHighlight': 'var(--lh-accent)',
                    '&:focus-within': {
                        boxShadow: '0 0 0 1px var(--lh-accent)',
                        borderColor: 'var(--lh-accent)',
                    },
                    '& input': {
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        fontSize: '12px',
                        lineHeight: '100%',
                        boxSizing: 'border-box',
                    },
                    '& input::placeholder': {
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        fontSize: '12px',
                        opacity: 1,
                        color: 'var(--lh-fg-muted)',
                    },
                }}
            />

            <Popover
                open={isOpen}
                anchorEl={anchorRef.current}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    '& .MuiPopover-paper': {
                        mt: 0.5,
                        p: 2,
                        width: 320,
                        boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
                        bgcolor: 'var(--joy-palette-background-surface, #fff)',
                        color: 'var(--joy-palette-text-primary, #171a1c)',
                    },
                }}
            >
                {/* Month Navigation */}
                <div className="flex justify-between items-center mb-4">
                    <IconButton size="sm" onClick={handlePrevMonth}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography level="title-md" fontWeight={600}>
                        {monthName}
                    </Typography>
                    <IconButton size="sm" onClick={handleNextMonth}>
                        <ChevronRightIcon />
                    </IconButton>
                </div>

                {/* Week Days Header */}
                <div className="grid grid-cols-7 mb-1">
                    {weekDays.map((day) => (
                        <div key={day} className="text-center text-[11px] font-semibold text-gray-500 dark:text-gray-400 py-0.5">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7">
                    {days.map((day, index) => {
                        if (day === null) {
                            return <div key={`empty-${index}`} />
                        }

                        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
                        date.setHours(0, 0, 0, 0)
                        const isDisabled = isDateDisabled(date)
                        const selected = isDateSelected(date)
                        const inRange = isDateInRange(date)

                        return (
                            <div
                                key={day}
                                onClick={() => !isDisabled && handleDateClick(day)}
                                className={cn(
                                    'text-center py-2 text-sm flex items-center justify-center w-full',
                                    selected && 'bg-blue-600 text-white font-semibold rounded-lg',
                                    !selected && inRange && 'bg-blue-50 dark:bg-blue-900/30',
                                    isDisabled && 'opacity-30 cursor-not-allowed',
                                    !isDisabled && !selected && 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700',
                                    !selected && !isDisabled && 'text-gray-900 dark:text-gray-100'
                                )}
                            >
                                {day}
                            </div>
                        )
                    })}
                </div>
            </Popover>
        </div>
    )
}
